// Shared insurance-split helper for the Tx Presenter patient-facing surfaces
// (Itemized view, Arch & Region view, Plan Summary, Present Mode topbar).
//
// The backend now returns per-procedure primary/secondary insurance estimates
// (insEstPrimary / insEstSecondary — numbers, >= 0). Older responses and old
// signed-PDF snapshots predate those fields, so we fall back to the historical
// derivation: a single combined "insurance covers" amount = max(0, fee −
// patientFee), attributed entirely to primary.
//
// KEEP IN SYNC with tx-presenter-service/lib/pdf/format.js insuranceSplit —
// the signed-PDF renderer computes the same split so the on-screen plan and the
// PDF agree line-for-line.
//
// Returns { primary, secondary, total } (all >= 0). Secondary is only ever > 0
// when the explicit field is present and positive; callers gate the "Secondary
// ins." sub-line on secondary > 0 so single-insurance patients render exactly
// as before.
export function insuranceSplit(p) {
  const proc = p || {};
  if (proc.insEstPrimary !== undefined && proc.insEstPrimary !== null) {
    const primary = Math.max(0, parseFloat(proc.insEstPrimary) || 0);
    const secondary = Math.max(0, parseFloat(proc.insEstSecondary) || 0);
    return { primary, secondary, total: primary + secondary };
  }
  // Fallback (old backend / old snapshot): combined single line on primary.
  const total = Math.max(0, (parseFloat(proc.fee) || 0) - (parseFloat(proc.patientFee) || 0));
  return { primary: total, secondary: 0, total };
}

// The UCR — Open Dental's "Fee" column, before any PPO write-off.
//
// Falls back to `fee` when procFee is missing or zero, so an old snapshot can
// never render a $0 "Total Fee".
//
// KEEP IN SYNC with tx-presenter-service/lib/pdf/format.js ucrOf.
export function ucrOf(p) {
  const proc = p || {};
  const ucr = parseFloat(proc.procFee) || 0;
  const fee = parseFloat(proc.fee) || 0;
  return ucr > 0 ? ucr : fee;
}

// The PPO write-off — Open Dental's "Discount" column, i.e. the gap between the
// UCR and the negotiated fee the office actually collects.
//
// This is NOT `p.discount`: that is the membership/discount-plan reduction
// (procedurelog.Discount), a different quantity that can apply at the same time.
//
// Prefers the backend's `writeOff` field and derives `UCR − discount − fee` for
// responses and signed-PDF snapshots that predate it (2026-09-09), so a subtotal
// can never fail to add up.
//
// KEEP IN SYNC with tx-presenter-service/lib/pdf/format.js writeOffOf.
export function writeOffOf(p) {
  const proc = p || {};
  if (proc.writeOff !== undefined && proc.writeOff !== null) {
    return Math.max(0, parseFloat(proc.writeOff) || 0);
  }
  return Math.max(0, ucrOf(proc) - (parseFloat(proc.discount) || 0) - (parseFloat(proc.fee) || 0));
}
